import { Skeleton } from '@teach/ui'
import { useRevealOnScroll } from '@/lib/useRevealOnScroll'
import type { Instruction } from '../types'
import { ArticlePickTile } from './ArticlePickTile'
import { ArticlesEmpty, CreateArticleButton } from './ArticlesEmpty'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  isLoading: boolean
  isError: boolean
  items: Instruction[]
  debouncedQ: string
  selectedId: number | null
  onSelect: (id: number) => void
  /** Обёртка пустого состояния — например, по центру высокой панели. */
  emptyClassName?: string
}

/** Карточки статей по две в ряд, без страниц: подгружаются при прокрутке. */
export const ArticlePickerResults = ({
  isLoading,
  isError,
  items,
  debouncedQ,
  selectedId,
  onSelect,
  emptyClassName,
}: Props) => {
  const m = useMessages(messages)
  const { shown, hasMore, sentinelRef } = useRevealOnScroll(items.length, debouncedQ, 12)

  if (isLoading) {
    return (
      <div className="mt-5 grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-[4/5] w-full rounded-2xl" />
        ))}
      </div>
    )
  }

  if (isError) {
    return <div className="py-12 text-center text-muted-foreground">{m.pickerLoadFailed}</div>
  }

  if (items.length === 0) {
    return debouncedQ ? (
      <div className="py-12 text-center text-muted-foreground">{m.nothingFound}</div>
    ) : (
      <div className={emptyClassName}>
        <ArticlesEmpty />
      </div>
    )
  }

  return (
    <>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {items.slice(0, shown).map((article) => (
          <ArticlePickTile
            key={article.id}
            article={article}
            selected={selectedId === article.id}
            onSelect={onSelect}
          />
        ))}
      </div>
      {hasMore ? <div ref={sentinelRef} aria-hidden className="h-px" /> : null}
      {/* Нужной статьи нет — создать её тут же, в соседней вкладке. */}
      <CreateArticleButton className="mt-5 w-full" />
    </>
  )
}
